import { LoadingButton } from '@mui/lab'
import {
  Alert,
  Button,
  DialogActions,
  DialogContent,
  Grid,
  Stack,
} from '@mui/material'
import { Form, FormikProvider, useFormik } from 'formik'
import { useMemo } from 'react'
import { toast } from 'react-toastify'
import * as yup from 'yup'

import DialogWrapper from '~/components/dialogWrapper'
import FormikLabeledAutoComplete from '~/components/formikFields/formikLabeledAutocomplete'
import FormikLabeledSelect from '~/components/formikFields/formikLabeledSelect'
import FormikLabeledTextField from '~/components/formikFields/formikLabeledTextField'
import useBulkAssignAgents from '~/features/agents/hooks/useBulkAssignAgents'
import useCreateLocations from '~/features/agents/hooks/useCreateLocations'
import useGetAgentsByOrgId from '~/features/agents/hooks/useGetAgentsByOrgId'
import useTimezone from '~/features/agents/hooks/useTimezone'
import useUpdateLocations from '~/features/agents/hooks/useUpdateLocations'
import { EAgentKeys, ELocationKeys, TLocation } from '~/features/agents/models'
import AssistantMultiSelect, {
  TAssistantOption,
} from '~/features/locations/components/assistantMultiSelect'
import {
  getAgentAssignmentChanges,
  getCreateLocationPayload,
  getEditLocationPayload,
} from '~/features/locations/components/createEditLocationModal/utils'
import {
  LOCATION_CITY_MAX_LENGTH,
  LOCATION_STATE_MAX_LENGTH,
  LOCATION_STREET_ADDRESS_1_MAX_LENGTH,
  LOCATION_STREET_ADDRESS_2_MAX_LENGTH,
  LOCATION_TYPE_OPTIONS,
  LOCATION_ZIP_CODE_MAX_LENGTH,
} from '~/features/locations/constants'
import { ELocationFormKeys, TLocationForm } from '~/features/locations/models'
import {
  getDefaultLocationFormValues,
  locationValidationSchema,
} from '~/features/locations/utils'
import { store } from '~/store'
import rem from '~/utils/ui'

type TCreateEditLocationForm = TLocationForm & {
  agentIds: string[]
}

const validationSchema = locationValidationSchema.shape({
  agentIds: yup.array().of(yup.string().required()).required(),
})

type TProps = {
  initialName?: string
  isOpen: boolean
  location?: TLocation
  onClose: () => void
  onSuccess?: (locationId: string) => void
  subtitle?: string
}

function CreateEditLocationModal({
  initialName = '',
  isOpen,
  location = undefined,
  onClose,
  onSuccess = undefined,
  subtitle = undefined,
}: TProps) {
  const { mutateAsync: createLocations } = useCreateLocations()
  const { mutateAsync: updateLocations } = useUpdateLocations()
  const { mutateAsync: bulkAssignAgents } = useBulkAssignAgents()
  const { options: timezoneOptions } = useTimezone()
  const isEditMode = Boolean(location)
  const orgId = store((state) => state.user.organizationId) || ''
  const {
    data: agentsResponse,
    isError: isAgentsError,
    isLoading: isLoadingAgents,
  } = useGetAgentsByOrgId({
    orgId,
    queryOptions: { enabled: isOpen && isEditMode && !!orgId },
  })

  const assistantOptions = useMemo<TAssistantOption[]>(() => {
    const optionsById = new Map(
      (agentsResponse?.data ?? []).map((agent) => [
        agent[EAgentKeys.id],
        {
          label:
            agent[EAgentKeys.agentReferenceName] ||
            agent[EAgentKeys.agentName] ||
            agent[EAgentKeys.agentEmail] ||
            agent[EAgentKeys.id],
          value: agent[EAgentKeys.id],
        },
      ]),
    )

    location?.[ELocationKeys.agents].forEach((agent) => {
      if (!optionsById.has(agent.id)) {
        optionsById.set(agent.id, {
          label: agent.agentReferenceName || agent.id,
          value: agent.id,
        })
      }
    })

    return [...optionsById.values()].sort((optionA, optionB) =>
      optionA.label.localeCompare(optionB.label),
    )
  }, [agentsResponse?.data, location])

  const initialValues = useMemo<TCreateEditLocationForm>(() => {
    if (!location) {
      return {
        ...getDefaultLocationFormValues(),
        [ELocationFormKeys.name]: initialName,
        agentIds: [],
      }
    }

    return {
      [ELocationFormKeys.name]: location[ELocationKeys.name] || '',
      [ELocationFormKeys.streetAddress1]:
        location[ELocationKeys.streetAddress1] || '',
      [ELocationFormKeys.streetAddress2]:
        location[ELocationKeys.streetAddress2] || '',
      [ELocationFormKeys.city]: location[ELocationKeys.city] || '',
      [ELocationFormKeys.state]: location[ELocationKeys.state] || '',
      [ELocationFormKeys.zipCode]: location[ELocationKeys.zipCode] || '',
      [ELocationFormKeys.timezone]: location[ELocationKeys.timezone] || '',
      [ELocationFormKeys.hoursText]: location[ELocationKeys.hoursText] || '',
      [ELocationFormKeys.type]: location[ELocationKeys.type] || '',
      agentIds: location[ELocationKeys.agents].map((agent) => agent.id),
    }
  }, [initialName, location])

  const formik = useFormik<TCreateEditLocationForm>({
    // Saving details refetches the location, and reinitializing on that would
    // drop assignments left pending by a failed second step. Create mode keeps
    // syncing so a typed-in name still lands in the form.
    enableReinitialize: !isEditMode || !isOpen,
    initialValues,
    validationSchema,
    onSubmit: async (values, { resetForm: resetSubmittedForm }) => {
      if (isEditMode && location) {
        const locationPayload = getEditLocationPayload({
          initialValues,
          locationId: location[ELocationKeys.id],
          values,
        })
        const assignments = getAgentAssignmentChanges({
          initialAgentIds: initialValues.agentIds,
          locationId: location[ELocationKeys.id],
          selectedAgentIds: values.agentIds,
        })
        const hasLocationChanges = Object.keys(locationPayload).some(
          (key) => key !== ELocationKeys.id,
        )

        // The details and the assignments are two separate requests, so they run
        // one after the other to avoid reassigning assistants to a location
        // whose own update just failed.
        if (hasLocationChanges) {
          try {
            await updateLocations({ locations: [locationPayload] })
          } catch {
            // useUpdateLocations already surfaces the reason.
            return
          }
        }

        if (assignments.length) {
          try {
            await bulkAssignAgents({ assignments })
          } catch {
            toast.error(
              hasLocationChanges
                ? 'Location details were saved, but the assistant assignments were not. Please try again.'
                : 'Could not update the assistant assignments. Please try again.',
            )
            return
          }
        }

        toast.success('Location updated successfully')
        resetSubmittedForm()
        onClose()
        return
      }

      await createLocations(
        {
          payload: {
            locations: [getCreateLocationPayload(values)],
          },
        },
        {
          onSuccess: (response) => {
            const createdLocationId = response.data?.created?.[0]?.id

            if (createdLocationId) {
              onSuccess?.(createdLocationId)
            }

            toast.success('Location created successfully')
            resetSubmittedForm()
            onClose()
          },
        },
      )
    },
  })

  const { dirty, isSubmitting, isValid, resetForm } = formik
  const isSaveDisabled = isEditMode
    ? !dirty || !isValid || isSubmitting
    : !isValid || isSubmitting

  const handleClose = () => {
    resetForm()
    onClose()
  }

  const dialogSubtitle =
    subtitle ??
    (isEditMode
      ? undefined
      : 'Add location details. Assistants will inherit these settings.')

  return (
    <DialogWrapper
      isOpen={isOpen}
      onClose={handleClose}
      subtitle={dialogSubtitle}
      sx={{
        '& > .MuiDialog-container > .MuiPaper-root': {
          maxWidth: rem(isEditMode ? 672 : 512),
        },
      }}
      title={isEditMode ? 'Edit Location' : 'Add Location'}
    >
      <FormikProvider value={formik}>
        <Stack
          component={Form}
          spacing={3}
          sx={{
            overflow: 'hidden',
            paddingBlock: rem(16),
            marginTop: rem(16),
            height: '100%',
          }}
        >
          <DialogContent
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: rem(16),
              padding: 0,
            }}
          >
            <FormikLabeledTextField
              label="Location Name"
              name={ELocationFormKeys.name}
              placeholder="e.g., Downtown Fitness"
              required
            />

            <FormikLabeledAutoComplete
              label="Timezone"
              name={ELocationFormKeys.timezone}
              options={timezoneOptions}
              placeholder="e.g., America/Los_Angeles"
            />

            <Grid container spacing={rem(12)}>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledTextField
                  label="Address Line 1"
                  name={ELocationFormKeys.streetAddress1}
                  placeholder="e.g., 123 Main Street"
                  inputProps={{
                    maxLength: LOCATION_STREET_ADDRESS_1_MAX_LENGTH,
                  }}
                />
              </Grid>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledTextField
                  label="Address Line 2"
                  name={ELocationFormKeys.streetAddress2}
                  placeholder="e.g., 123 Main Street"
                  inputProps={{
                    maxLength: LOCATION_STREET_ADDRESS_2_MAX_LENGTH,
                  }}
                />
              </Grid>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledTextField
                  label="City"
                  name={ELocationFormKeys.city}
                  placeholder="e.g., Los Angeles"
                  inputProps={{ maxLength: LOCATION_CITY_MAX_LENGTH }}
                />
              </Grid>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledTextField
                  label="State"
                  name={ELocationFormKeys.state}
                  placeholder="e.g., CA"
                  inputProps={{ maxLength: LOCATION_STATE_MAX_LENGTH }}
                />
              </Grid>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledTextField
                  label="Zip code"
                  name={ELocationFormKeys.zipCode}
                  placeholder="e.g., 90001"
                  inputProps={{ maxLength: LOCATION_ZIP_CODE_MAX_LENGTH }}
                />
              </Grid>
              <Grid item xxxs={6} sx={{ width: '100%' }}>
                <FormikLabeledSelect
                  label="Location Type"
                  name={ELocationFormKeys.type}
                  options={LOCATION_TYPE_OPTIONS}
                  required
                />
              </Grid>
              {isEditMode && (
                <Grid item xxxs={6} sx={{ width: '100%' }}>
                  <AssistantMultiSelect
                    isLoading={isLoadingAgents}
                    label="Assistants"
                    limitTags={-1}
                    name="agentIds"
                    options={assistantOptions}
                  />
                </Grid>
              )}
              {!isEditMode && (
                <Grid item xxxs={12} sx={{ width: '100%' }}>
                  <FormikLabeledTextField
                    label="Hours"
                    maxRows={25}
                    minRows={3}
                    multiline
                    name={ELocationFormKeys.hoursText}
                    placeholder="e.g., 5am - 10pm"
                  />
                </Grid>
              )}
            </Grid>
            {isEditMode && isAgentsError && (
              <Alert severity="warning">
                Assistants could not be loaded. Existing assignments are still
                shown, but additional assistants may be unavailable.
              </Alert>
            )}
          </DialogContent>

          <DialogActions sx={{ padding: 0 }}>
            <Button
              disabled={isSubmitting}
              type="button"
              variant="tertiary1SecondaryModern"
              onClick={handleClose}
            >
              Cancel
            </Button>
            <LoadingButton
              disabled={isSaveDisabled}
              loading={isSubmitting}
              loadingPosition="start"
              type="submit"
              variant="primaryModern"
            >
              {isEditMode ? 'Save Changes' : 'Add Location'}
            </LoadingButton>
          </DialogActions>
        </Stack>
      </FormikProvider>
    </DialogWrapper>
  )
}

export default CreateEditLocationModal
